import React, {useState, useEffect, useMemo} from "react";
import {motion} from "framer-motion";
import {Handshake, Search} from "lucide-react";
import {toast} from "react-toastify";
import {searchOrganizations, createOrgInvite} from "../api";
import {CreateInviteModal, CopyToast} from "./OrgInvitesPopup";
import Table1 from "../../../../components/ACS-Dashboard/ACS-Blocks/TableSecondary";
import Cookies from "js-cookie";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";

const AddPreferredPartner = ({onClose, onSubmit}) => {
    const [query, setQuery] = useState("");
    const [partners, setPartners] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [loading, setLoading] = useState(false);

    const [showInvite, setShowInvite] = useState(false);
    const [inviteForm, setInviteForm] = useState({days: 7, email: "", scacCode: ""});
    const [inviteLoading, setInviteLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    // 🔍 Debounced org search
    useEffect(() => {
        const delay = setTimeout(async () => {
            if (!query.trim()) {
                setPartners([]);
                return;
            }
            setLoading(true);
            try {
                const res = await searchOrganizations(query);
                const mapped = Array.isArray(res.data?.content)
                    ? res.data.content.map((org) => ({
                        name: org.name || "Unnamed",
                        officeCode: org.officeCode,
                        location: org.location,
                        email: org.email,
                        phone: org.phone,
                    }))
                    : [];
                setPartners(mapped);
            } catch (err) {
                console.error("Search failed:", err);
                toast.error("Failed to fetch partners");
            } finally {
                setLoading(false);
            }
        }, 400);
        return () => clearTimeout(delay);
    }, [query]);

    // 📨 Invite modal
    const openInviteModal = () => {
        setInviteForm({days: 7, email: "", scacCode: ""});
        setShowInvite(true);
    };


    const handleInviteSubmit = async (e) => {
        e.preventDefault();
        setInviteLoading(true);
        try {
            const officeCode = Cookies.get("officeCode"); // ✅ get from cookie
            await createOrgInvite(
                officeCode,              // originalOfficeCode
                inviteForm.inviteType || "orgInvite",
                inviteForm.days,         // validUntilDate
                inviteForm.email || "",  // recipientEmail
                inviteForm.scacCode || "" // scacCode
            );
            setShowInvite(false);
            toast.success("Invitation sent successfully!");
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (err) {
            console.error("Invite failed:", err);
            toast.error("Failed to send invite");
        } finally {
            setInviteLoading(false);
        }
    };


    // 🔎 Filter
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return partners;
        return partners.filter((p) =>
            [p.name, p.officeCode].filter(Boolean).map(String).some((s) => s.toLowerCase().includes(q))
        );
    }, [partners, query]);

    // ➕ Add partner
    const handleAdd = async () => {
        if (!selectedPartner) {
            toast.warn("Please select a partner.");
            return;
        }
        try {
            await onSubmit(selectedPartner.officeCode);
            toast.success(`Added ${selectedPartner.name}`);
            onClose?.();
        } catch (err) {
            console.error("Add partner failed:", err);
            toast.error("Failed to add preferred partner");
        }
    };

    // 🔁 Toggle select/unselect
    const handleSelect = (partner) => {
        if (selectedPartner?.officeCode === partner.officeCode) {
            setSelectedPartner(null); // unselect
        } else {
            setSelectedPartner(partner);
        }
    };

    return (
        <motion.div
            className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center px-fluid-3 py-fluid-3"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
        >
            <motion.div
                className="bg-white rounded-2xl shadow-xl w-full max-w-[950px] max-h-[95vh] overflow-y-auto p-fluid-4 space-y-fluid-3"
                initial={{scale: 0.95}}
                animate={{scale: 1}}
                exit={{scale: 0.95}}
            >
                {/* === Header === */}
                <div className="flex flex-col items-center space-y-fluid-2 border-b pb-fluid-3">
                    <div className="bg-[#1e2a74] rounded-full p-fluid-2 text-white">
                        <Handshake size={30}/>
                    </div>
                    <h2 className="text-fluid-xl font-semibold text-[#111827]">Add Preferred Partner</h2>
                    <p className="text-fluid-base text-gray-600 text-center max-w-[600px]">
                        Search for an existing partner below or invite a new organization to join.
                    </p>
                </div>

                {/* === Search === */}
                <div className="flex items-center gap-3">
                    <label className="block text-fluid-sm font-medium text-gray-700 shrink-0">
                        Partner *
                    </label>
                    <div className="relative w-full">
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by name or office code..."
                            className="w-full border border-gray-300 rounded-xl pl-9 pr-3 py-2 text-fluid-sm focus:outline-none focus:border-[#223688] shadow-sm"
                        />
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#223688]"/>
                    </div>
                </div>

                {/* === Results Table === */}
                <div className="max-h-[55vh] overflow-y-auto mt-fluid-2">
                    {loading ? (
                        <Loader color="text-blue-600" message="Searching partners..."/>
                    ) : filtered.length > 0 ? (
                        <Table1
                            headers={["Select", "Name", "Office Code", "Location", "Phone", "Email"]}
                            columnWidths={["8%", "25%", "15%", "20%", "16%", "16%"]}
                            data={filtered}
                            renderRow={(row) => {
                                const isSelected = selectedPartner?.officeCode === row.officeCode;
                                return (
                                    <>
                                        <td
                                            className={`px-fluid-3 py-fluid-2 text-center ${
                                                isSelected ? "bg-blue-50" : ""
                                            }`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSelect(row);
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="partner"
                                                checked={!!isSelected}
                                                readOnly
                                                className="h-4 w-4 accent-[#1e2a74]"
                                            />
                                        </td>
                                        <td
                                            className={`px-fluid-3 py-fluid-2 text-fluid-sm text-[#030229] ${
                                                isSelected ? "bg-blue-50" : ""
                                            } cursor-pointer`}
                                            onClick={() => handleSelect(row)}
                                        >
                                            {row.name}
                                        </td>
                                        <td
                                            className={`px-fluid-3 py-fluid-2 text-fluid-sm font-roboto text-gray-700 ${
                                                isSelected ? "bg-blue-50" : ""
                                            } cursor-pointer`}
                                            onClick={() => handleSelect(row)}
                                        >
                                            {row.officeCode}
                                        </td>
                                        <td
                                            className={`px-fluid-3 py-fluid-2 text-fluid-sm text-gray-600 ${
                                                isSelected ? "bg-blue-50" : ""
                                            } cursor-pointer`}
                                            onClick={() => handleSelect(row)}
                                        >
                                            {row.location?.city && row.location?.state
                                                ? `${row.location.city}, ${row.location.state}`
                                                : "—"}
                                        </td>
                                        <td
                                            className={`px-fluid-3 py-fluid-2 text-fluid-sm text-gray-600 ${
                                                isSelected ? "bg-blue-50" : ""
                                            } cursor-pointer`}
                                            onClick={() => handleSelect(row)}
                                        >
                                            {row.phone || "—"}
                                        </td>
                                        <td
                                            className={`px-fluid-3 py-fluid-2 text-fluid-sm text-gray-600 truncate ${
                                                isSelected ? "bg-blue-50" : ""
                                            } cursor-pointer`}
                                            onClick={() => handleSelect(row)}
                                        >
                                            {row.email || "—"}
                                        </td>
                                    </>
                                );
                            }}
                        />
                    ) : query ? (
                        <div
                            onClick={openInviteModal}
                            className="p-5 text-blue-600 hover:bg-blue-50 cursor-pointer text-center border border-dashed border-blue-300 rounded-md"
                        >
                            ✉️ Invite "{query}" to join as a preferred partner
                        </div>
                    ) : (
                        <p className="p-4 text-gray-400 text-sm text-center">Start typing to search partners</p>
                    )}
                </div>

                {/* === Footer === */}
                <div className="flex justify-end gap-fluid-3 pt-fluid-3 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-fluid-button-x py-fluid-button-y border border-gray-300 rounded text-gray-700 hover:bg-gray-100 text-fluid-sm"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleAdd}
                        disabled={!selectedPartner}
                        className={`px-fluid-button-x py-fluid-button-y text-fluid-sm rounded font-semibold transition ${
                            !selectedPartner
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-[#1e2a74] hover:bg-[#18225c] text-white"
                        }`}
                    >
                        Add
                    </button>
                </div>
            </motion.div>

            {/* === Invite Modal === */}
            {showInvite && (
                <CreateInviteModal
                    form={inviteForm}
                    formLoading={inviteLoading}
                    onClose={() => setShowInvite(false)}
                    onChange={(f, v) => setInviteForm((prev) => ({...prev, [f]: v}))}
                    onSubmit={handleInviteSubmit}
                />
            )}

            {/* ✅ Success Toast */}
            {copied && <CopyToast message="Invitation sent successfully!"/>}
        </motion.div>
    );
};

export default AddPreferredPartner;
